import { useOptimistic, useState, useTransition } from 'react'
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react'
import { cn } from '../../lib/cn'
import { api } from '../../lib/api'
import type { FeedbackResponse, FeedbackSignal } from '../../lib/types'
import { Button, Chip } from '../ui'

// Structured reasons are persisted with the raw feedback signal for later
// analysis. A bare thumbs-down is far weaker than one with a reason.
const DOWN_REASONS = ['Too long', 'Too dark', 'Already played out', 'Not co-op enough', 'Not tonight']
const UP_REASONS = ['Loved the vibe', 'Right length', 'Great together', 'Been wanting this']

type Vote = 'up' | 'down' | null

function postFeedback(
  recommendationId: string,
  signal: FeedbackSignal,
  reason: string | undefined,
) {
  return api
    .post<FeedbackResponse>(`/recommendations/${recommendationId}/feedback`, {
      signal,
      reason: reason || undefined,
    })
    .catch(() => {
      // Reconcile: a failed call rolls the optimistic state back on next render.
    })
}

interface FeedbackControlsProps {
  recommendationId: string
  /** Passive outcome badge — playtime confirmed engagement (read-only signal). */
  outcomeMinutes?: number | null
}

export function FeedbackControls({ recommendationId, outcomeMinutes }: FeedbackControlsProps) {
  const [vote, setVote] = useState<Vote>(null)
  const [optimisticVote, setOptimisticVote] = useOptimistic(vote)
  const [reason, setReason] = useState<string | null>(null)
  const [, startTransition] = useTransition()

  const send = (signal: FeedbackSignal, reasonText?: string) => {
    startTransition(async () => {
      await postFeedback(recommendationId, signal, reasonText)
    })
  }

  const handleVote = (next: Vote) => {
    const resolved = vote === next ? null : next
    startTransition(() => {
      setOptimisticVote(resolved)
      setVote(resolved)
      setReason(null)
      if (resolved === 'up') send('thumbs_up')
      else if (resolved === 'down') send('thumbs_down')
    })
  }

  const handleReason = (r: string) => {
    const resolved = reason === r ? null : r
    setReason(resolved)
    if (resolved) send(optimisticVote === 'down' ? 'thumbs_down' : 'thumbs_up', resolved)
  }

  const reasons = optimisticVote === 'down' ? DOWN_REASONS : optimisticVote === 'up' ? UP_REASONS : []

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-text-muted">Did this land?</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-pressed={optimisticVote === 'up'}
            aria-label="Thumbs up"
            onClick={() => handleVote('up')}
            className={cn(
              // ≥44×44 effective target (§7) — icon buttons pad, never shrink.
              'inline-flex h-11 w-11 items-center justify-center rounded-control transition',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
              optimisticVote === 'up'
                ? 'bg-success/12 text-success-fg ring-1 ring-success/40'
                : 'text-text-muted hover:text-text hover:bg-surface',
            )}
          >
            <ThumbsUp className="h-4.5 w-4.5" strokeWidth={optimisticVote === 'up' ? 2.5 : 1.75} fill={optimisticVote === 'up' ? 'currentColor' : 'none'} />
          </button>
          <button
            type="button"
            aria-pressed={optimisticVote === 'down'}
            aria-label="Thumbs down"
            onClick={() => handleVote('down')}
            className={cn(
              'inline-flex h-11 w-11 items-center justify-center rounded-control transition',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
              optimisticVote === 'down'
                ? 'bg-danger/12 text-danger-fg ring-1 ring-danger/40'
                : 'text-text-muted hover:text-text hover:bg-surface',
            )}
          >
            <ThumbsDown className="h-4.5 w-4.5" strokeWidth={optimisticVote === 'down' ? 2.5 : 1.75} fill={optimisticVote === 'down' ? 'currentColor' : 'none'} />
          </button>
        </div>
      </div>

      {/* Reason follow-up — what makes the signal trainable. */}
      {reasons.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Why?">
          {reasons.map((r) => (
            <Chip key={r} selected={reason === r} onToggle={() => handleReason(r)}>
              {r}
            </Chip>
          ))}
        </div>
      )}

      {outcomeMinutes != null && outcomeMinutes > 0 && (
        <span className="inline-flex items-center gap-1.5 text-sm text-success-fg">
          <Check className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
          They played it {Math.round(outcomeMinutes / 60)}h
        </span>
      )}
    </div>
  )
}

interface PlayingThisButtonProps {
  recommendationId: string
}

/**
 * The card's ONE primary action (§6.2/§6.5): commits the group ("We're playing
 * this") and sends the `playing_this` intent signal. Confirmed state flips to a
 * calm secondary treatment so amber stays with the un-taken action (§1.4).
 */
export function PlayingThisButton({ recommendationId }: PlayingThisButtonProps) {
  const [sent, setSent] = useState(false)
  const [, startTransition] = useTransition()

  return (
    <Button
      type="button"
      variant={sent ? 'secondary' : 'primary'}
      aria-pressed={sent}
      onClick={() => {
        if (sent) return
        setSent(true)
        startTransition(async () => {
          await postFeedback(recommendationId, 'playing_this', undefined)
        })
      }}
    >
      {sent && <Check className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />}
      {sent ? "We're on it" : "We're playing this"}
    </Button>
  )
}
